import * as logger from 'loglevel';
import { getUuidMin } from '@front10/helpers/dist/uuid';
import { getDateRangeAsDates } from '../../helpers/localization/localizationUtils';
import transformRequestSmartSearch from './transformRequest/transformSmartSearchFares';
import axiosWrapper from '../../helpers/axiosWrapper';
import { yearAdjustmentPatch } from './yearAdjustmentPatch';
import { NO_DURATION_DEFINED } from '../../helpers/axiosWrapper/cache';
/**
 * Makes sure the fares response is complain with a basic schema
 * @param {*} fares
 */
const validateFaresResponse = (fares) => {
  const validation = { isValid: true, message: '' };
  if (!fares || !Array.isArray(fares)) {
    validation.isValid = false;
    return {
      isValid: false,
      data: fares,
      message: 'INTEGRATION__FARES: Fares must be an Array'
    };
  }
  if (fares.length > 0) {
    const fareItem = fares[0];
    if (!fareItem.outboundFlight.origin || !fareItem.outboundFlight.destination) {
      return {
        isValid: false,
        data: fareItem,
        message:
          'INTEGRATION__FARES: FareItem SCHEMA is invalid, please check Fares API documentation.'
      };
    }
  }
  return validation;
};

/**
 * Here we find the lowest fare
 */
const lowestFare = (data) => {
  const lowest = Math.min(...data.map((item) => item.priceSpecification.totalPrice));
  return lowest;
};

/**
 * The group-routes API does not returns the journeyType
 * so this map allows us to use query sent parameters to do it
 */
const journeyTypeMap = {
  ROUND_TRIP: 'rt',
  ONE_WAY: 'ow',
  MULTI_CITY: 'mc',
  EACH_WAY: 'ew'
};

/**
 * The group routes API returns the data in non linear format
 * so this function will intend normalize the data for the components
 * @param {*} routesRawData
 * @param {*} query
 */
const transformData = (rawData, query) => {
  const { uiDatesYearsAdjustmentPatch, paramsTrackingExtra } = query;
  const lowestRate = lowestFare(rawData);
  const fares = rawData.map((fare, indexPosition) => {
    let { searchDate } = fare;
    const {
      airline: { iataCode: airlineCode },
      priceSpecification: {
        currencyCode,
        formattedTotalPrice,
        totalPrice,
        usdTotalPrice,
        redemption
      },
      departureDate,
      formattedDepartureDate,
      returnDate,
      formattedReturnDate,
      outboundFlight: {
        destination: {
          airport: { image: destinationAirportImage },
          state: { image: destinationStateImage },
          city: { name: destinationCity, image: destinationCityImage },
          country: { image: destinationCountryImage },
          region: { image: destinationRegionImage }
        },
        origin: {
          city: { name: originCity }
        },
        arrivalAirportIataCode: destination,
        departureAirportIataCode: origin,
        fareClass: farenetTravelClass,
        fareClassInput
      },
      flightDeltaDays, // FareItemTripDuration este campo no viene del endpoint
      travelClass,
      journeyType
    } = fare;
    // Workaround for Safari and IE
    if (searchDate && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+[+|-]\d{4}/.test(searchDate)) {
      searchDate = `${searchDate.substring(0, searchDate.length - 2)}:${searchDate.substring(
        searchDate.length - 2
      )}`;
    }
    if (redemption && formattedTotalPrice) {
      fare.redemption = redemption;
    }
    const newFare = {
      airlineCode: airlineCode.toLowerCase(),
      currencyCode,
      formattedTotalPrice,
      totalPrice,
      usdTotalPrice,
      redemption,
      departureDate,
      formattedDepartureDate,
      returnDate,
      formattedReturnDate,
      destination,
      destinationCity,
      origin,
      originCity,
      farenetTravelClass,
      fareClassInput,
      flightDeltaDays,
      travelClass,
      paramsTrackingExtra, //Support Performance Tracking parameters to be sent to the Booking Pop-up so the customer can track the module conversion.
      journeyType: journeyTypeMap[journeyType],
      searchDate,
      index: getUuidMin(
        `${airlineCode}${currencyCode}${departureDate}${destination}${farenetTravelClass}${flightDeltaDays}${formattedDepartureDate}${formattedTotalPrice}${origin}${totalPrice}${travelClass}${usdTotalPrice}`
      ),
      destinationImage:
        destinationAirportImage ||
        destinationCityImage ||
        destinationStateImage ||
        destinationCountryImage ||
        destinationRegionImage,
      isLowest: totalPrice === lowestRate,
      positionFromApi: indexPosition
    };
    yearAdjustmentPatch(newFare, uiDatesYearsAdjustmentPatch);
    return newFare;
  });

  return fares;
};

/**
 * Retrieve fares from the GroupRoutes Endpoint
 * @param {*} filter
 * @param {*} integrationSettings
 */
const getFares = (filter, integrationSettings, meta, pageContext) => {
  return new Promise((resolve, reject) => {
    if (!integrationSettings) {
      const MESSAGE = `INTEGRATION__FARES: **integrationSettings** not specified.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }
    const { v3SearchFaresSmart: faresApiSettings } = integrationSettings;
    const { cache = false, cacheDuration = NO_DURATION_DEFINED } = faresApiSettings;
    const filterCopy = { ...filter };

    if (
      !faresApiSettings ||
      !faresApiSettings.method ||
      !faresApiSettings.endPoint ||
      !faresApiSettings.headers
    ) {
      const MESSAGE = `INTEGRATION__FARES: **integrationSettings.faresGroupRoutes** configuration issues.`;
      logger.error(MESSAGE);
      reject(MESSAGE);
    }

    filterCopy.departure = getDateRangeAsDates(
      filterCopy.departure,
      'YYYY-MM-DD',
      'INTEGRATION__FARES'
    );
    if (filterCopy.return) {
      filterCopy.return = getDateRangeAsDates(
        filterCopy.return,
        'YYYY-MM-DD',
        'INTEGRATION__FARES'
      );
    }

    axiosWrapper(
      {
        method: faresApiSettings.method,
        url: `${faresApiSettings.endPoint}`,
        headers: { ...faresApiSettings.headers },
        data: transformRequestSmartSearch(filterCopy)
      },
      {
        resource: 'INTEGRATION__SMART__FARES__SEARCH',
        meta,
        integrationSettings,
        pageContext,
        cache,
        cacheDuration
      }
    )
      .then((response) => {
        const validation = validateFaresResponse(response.data);
        if (validation.isValid) {
          logger.info('INTEGRATION__FARES: Successful request', response.data);
          const fares = transformData(response.data, filter);
          resolve(fares);
        } else {
          logger.error(validation.message, validation.data);
          reject(response.data);
        }
      })
      .catch((err) => {
        reject(err);
      });
  });
};

export { getFares, transformData };
